import React from 'react';
import classNames from 'classnames';
import { Img } from 'react-image';

// Types
import { ReferencePatternName } from 'Shared/types/BaInfo.types';

// Components
import ReferencePatternIcon from 'Shared/components/ReferencePatternIcon/ReferencePatternIcon';

// Styles
import './WpAvatar.scss';

export type Size = 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'xxxl';
export const DEFAULT_SIZE = 'md';
export const BASE_CLASS_NAME = 'WpAvatar';

export interface Props {
  size?: Size;
  disabled?: boolean;
  avatar: {
    color: string;
    initials: string;
    profilePictureUrl?: string;
  };
  referencePattern?: ReferencePatternName;
  showReferencePatternIcon?: boolean;
}

const WpAvatar: React.FC<Props> = ({
  avatar,
  disabled = false,
  size = DEFAULT_SIZE,
  referencePattern,
  showReferencePatternIcon = false,
}: Props) => {
  const userInitials = (
    <div className="WpAvatar__initials" style={{ backgroundColor: avatar.color }}>
      {avatar.initials}
    </div>
  );

  // react-img has an issue not consistently rendering base64
  // encoded images. Check to see if image string is base64
  // and if so, ue plain html image element - this will mostly
  // come up in the edit avatar photo
  const regex = /^data:image\/(?:jpg|png|jpeg)(?:;charset=utf-8)?;base64,(?:[A-Za-z0-9]|[+/])+={0,2}/;
  const isBase64 = avatar.profilePictureUrl && regex.test(avatar.profilePictureUrl);

  const includeReferencePatternIcon = showReferencePatternIcon && referencePattern && size !== 'md';

  const baseClassName = BASE_CLASS_NAME;
  const classes = classNames(baseClassName, `${baseClassName}--${size}`, disabled && `${baseClassName}--disabled`);

  return (
    <div className={classes}>
      <div className={`${baseClassName}__user-photo`}>
        {isBase64 ? (
          <img className={`${baseClassName}--img`} src={avatar.profilePictureUrl} />
        ) : (
          <Img
            className={`${baseClassName}--img`}
            src={avatar.profilePictureUrl || '#'}
            loader={userInitials}
            unloader={userInitials}
          />
        )}
      </div>
      {includeReferencePatternIcon && (
        <ReferencePatternIcon
          referencePattern={referencePattern}
          className={`${baseClassName}__reference-pattern-icon`}
        />
      )}
    </div>
  );
};

export default WpAvatar;
